import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getPaymentConfig, updatePaymentConfig } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/payments")({
  component: AdminPayments,
});

function AdminPayments() {
  const load = useServerFn(getPaymentConfig);
  const save = useServerFn(updatePaymentConfig);
  const qc = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ["payment-config"], queryFn: () => load() });

  const [form, setForm] = useState({
    enabled: true,
    mode: "live" as "live" | "sandbox",
    merchant_id: "",
    webhook_url: "",
    shipping_flat: 95,
    free_shipping_threshold: "" as string,
  });

  useEffect(() => {
    const s: any = data?.settings;
    if (!s) return;
    setForm({
      enabled: !!s.enabled,
      mode: s.mode === "sandbox" ? "sandbox" : "live",
      merchant_id: s.merchant_id ?? "",
      webhook_url: s.webhook_url ?? "",
      shipping_flat: Number(s.shipping_flat ?? 95),
      free_shipping_threshold: s.free_shipping_threshold == null ? "" : String(s.free_shipping_threshold),
    });
  }, [data]);

  const mut = useMutation({
    mutationFn: () =>
      save({
        data: {
          enabled: form.enabled,
          mode: form.mode,
          merchant_id: form.merchant_id || null,
          webhook_url: form.webhook_url || null,
          shipping_flat: Number(form.shipping_flat) || 0,
          free_shipping_threshold: form.free_shipping_threshold === "" ? null : Number(form.free_shipping_threshold),
        },
      }),
    onSuccess: () => { toast.success("Payment settings saved"); qc.invalidateQueries({ queryKey: ["payment-config"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Could not save settings"),
  });

  const webhookUrl = typeof window !== "undefined" ? `${window.location.origin}/api/public/payments/ikhokha/webhook` : "";

  if (isPending) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Payments</h1>
        <p className="text-sm text-muted-foreground">iKhokha online payment configuration for the shop.</p>
      </header>

      <section className="rounded-xl border bg-card p-5">
        <h2 className="text-sm font-semibold">Credentials</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Stored securely as encrypted secrets, never in the database or the browser.
        </p>
        <ul className="mt-3 space-y-2 text-sm">
          <CredRow label="IKHOKHA_APP_ID" ok={!!data?.credentials?.app_id} />
          <CredRow label="IKHOKHA_APP_SECRET" ok={!!data?.credentials?.app_secret} />
        </ul>
      </section>

      <section className="space-y-5 rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <Label>Accept online payments</Label>
            <p className="text-xs text-muted-foreground">Turn off to pause shop checkout.</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
        </div>

        <div>
          <Label>Environment</Label>
          <select
            value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value as "live" | "sandbox" })}
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="live">Live</option>
            <option value="sandbox">Sandbox (test)</option>
          </select>
        </div>

        <div>
          <Label>Merchant ID (optional reference)</Label>
          <Input value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} className="mt-1" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Delivery fee (R)</Label>
            <Input type="number" min={0} value={form.shipping_flat} onChange={(e) => setForm({ ...form, shipping_flat: Number(e.target.value) })} className="mt-1" />
          </div>
          <div>
            <Label>Free delivery over (R, blank for none)</Label>
            <Input type="number" min={0} value={form.free_shipping_threshold} onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })} className="mt-1" />
          </div>
        </div>

        <div>
          <Label>Webhook URL</Label>
          <Input value={form.webhook_url} placeholder={webhookUrl} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} className="mt-1" />
          <p className="mt-1 text-xs text-muted-foreground">
            Leave blank to use the default: <code>{webhookUrl}</code>. Give this URL to iKhokha support so payment results are confirmed automatically.
          </p>
        </div>

        <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
          {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save settings
        </Button>
      </section>
    </div>
  );
}

function CredRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <li className="flex items-center gap-2">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-destructive" />}
      <code className="text-xs">{label}</code>
      <span className="text-xs text-muted-foreground">{ok ? "configured" : "missing"}</span>
    </li>
  );
}
